// packages/report-components/src/pageFrameParts.tsx
//
// The two parts a chart frame and a table frame share on a report PAGE (plan
// 2026-09-19 report page house style, A6 + A8): the title ROW, and the window
// caption under it. One drawing for both frames, because both are budgeted by
// the same sum (`page_frame_chrome_px` in `layout.py`): padding, border, ONE
// title line, the gap, and one caption line when the block has a window of its
// own. A frame that drew its title differently would be a second number.

import * as React from "react";

import type { PageDensity } from "./PageDensityContext";
import { formatPeriod } from "./Period";
import { SmartTextLine, type SmartTextAnswer } from "./SmartText";

export type PageTitleRowProps = {
  density: PageDensity;
  title: React.ReactNode;
  /** Plain text of the title, for the `title` attribute of an ellipsised line. */
  titleText?: string;
  className: string;
  /** Sits at the right end of the title line (the reading room's provenance
   *  button). It never makes the row taller: the row is `frame_title_px`. */
  action?: React.ReactNode;
};

/** ONE line of exactly `frame_title_px`: the title, ellipsised past the
 *  frame's width, and an optional action at its right end. */
export function PageTitleRow({ density, title, titleText, className, action }: PageTitleRowProps): React.ReactElement {
  return (
    <div
      data-page-title-row="1"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: density.frame_title_px,
        minWidth: 0,
      }}
    >
      <div
        className={className}
        title={titleText}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          height: density.frame_title_px,
          lineHeight: `${density.frame_title_px}px`,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </div>
      {action ? (
        <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", height: density.frame_title_px }}>
          {action}
        </span>
      ) : null}
    </div>
  );
}

export type WindowCaptionProps = {
  /** The VALIDATED period key of a block that renders a window other than the
   *  report's (W8, ADR-0286). Words come from `formatPeriod`. */
  period: string;
  density: PageDensity | null;
  className: string;
};

/** "Month to date", "Last 13 weeks": the window of a block that has its own.
 *  One fixed `caption_line_px` line on a page (the budget counts it); a
 *  natural line off a page. */
export function WindowCaption({ period, density, className }: WindowCaptionProps): React.ReactElement {
  return (
    <div
      className={className}
      data-window-caption="1"
      style={{
        fontSize: "var(--verity-font-size-xs, 12px)",
        fontWeight: 500,
        color: "var(--verity-color-primary, #3455D3)",
        ...(density
          ? {
              height: density.caption_line_px,
              lineHeight: `${density.caption_line_px}px`,
              overflow: "hidden",
              whiteSpace: "nowrap" as const,
              textOverflow: "ellipsis",
            }
          : { marginTop: 2 }),
      }}
    >
      {formatPeriod(period)}
    </div>
  );
}

export type BlockSubtitleProps = {
  /** The author's template: one paragraph, plain or with `{{name}}`. */
  content: string;
  /** The subtitle's own hook answer (`smart_text` shape); absent for a plain
   *  subtitle. */
  block?: SmartTextAnswer;
  density: PageDensity | null;
  className: string;
};

/** A chart or table block's subtitle (plan 2026-10-16-a-block-subtitle):
 *  muted, clamped to two lines. On a page its box is EXACTLY the two lines
 *  the server budgets (`page_frame_chrome_px(subtitle=True)`), so a short
 *  subtitle leaves the same room and a long one never pushes the canvas;
 *  `max-height` + `overflow: hidden` hold it where `-webkit-line-clamp` is
 *  not drawn (Outlook). Off a page: a natural clamp of two lines. */
export function BlockSubtitle({ content, block, density, className }: BlockSubtitleProps): React.ReactElement {
  const lines = density ? density.subtitle_lines : 2;
  const box = density ? density.subtitle_line_px * density.subtitle_lines : undefined;
  return (
    <div
      className={className}
      data-block-subtitle="1"
      title={content}
      style={{
        fontSize: density ? density.subtitle_font_px : "var(--verity-font-size-xs, 12px)",
        color: "var(--verity-color-text-muted, #6b7280)",
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical" as const,
        overflow: "hidden",
        overflowWrap: "anywhere",
        ...(density
          ? { lineHeight: `${density.subtitle_line_px}px`, height: box, maxHeight: box }
          : { marginTop: 2 }),
      }}
    >
      <SmartTextLine content={content} block={block} />
    </div>
  );
}

export type PageEyebrowProps = {
  density: PageDensity;
  /** The label itself: the block's own title on a page (the server budgets
   *  its length, `layout.eyebrow_chars_for_cell`). */
  children: React.ReactNode;
  className?: string;
  /** Plain text for the `title` attribute of a line that may ellipsise. */
  text?: string;
};

/** ONE line of `eyebrow_line_px` with its gap under it: the small label above
 *  a reading. One drawing for the executive summary and the focus band, so
 *  the page cannot grow two eyebrows that differ by a pixel. */
export function PageEyebrow({ density, children, className, text }: PageEyebrowProps): React.ReactElement {
  return (
    <h2
      className={className}
      title={text}
      style={{
        margin: 0,
        marginBottom: density.eyebrow_gap_px,
        height: density.eyebrow_line_px,
        lineHeight: `${density.eyebrow_line_px}px`,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        fontSize: "var(--verity-font-size-xs, 12px)",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "var(--verity-color-primary, #3455D3)",
      }}
    >
      {children}
    </h2>
  );
}
